import { useEffect, useMemo, useRef, useState } from 'react'
import 'quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import Quill from 'quill'
import { MentionBlot, Mention } from 'quill-mention'
import useApiService from 'services/api'
import { useAuth } from 'context/authentication/useAuth'
import './Editor.css'

// Register Quill modules
Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention })

export interface EditorProps {
  value: string
  onChange?: (value: string | undefined) => void
}

export interface CommonEditorProps {
  theme?: 'snow' | 'bubble'
  placeholder?: string
  label?: string
  optional?: boolean
  additionalToolbarButtons?: React.ReactNode
  /** Content rendered above the toolbar row. */
  aboveToolbar?: React.ReactNode
  /** Content rendered before the toolbar (e.g. a Send button). */
  toolbarPrefix?: React.ReactNode
  /** Content rendered after the toolbar, pushed to the right (e.g. a Cancel link). */
  toolbarSuffix?: React.ReactNode
  /** Additional className applied to the toolbar wrapper (e.g. background color). */
  toolbarClassName?: string
  readOnly?: boolean
  disableToolbar?: boolean
  /**
   * Restrict the editor to plain text. Strips bold, italic, color, links,
   * images, etc. on input and paste. Useful for fields that ship as the
   * `text/plain` MIME part of an email — formatting there doesn't render
   * (recipients see raw `<strong>` tags or, at best, stripped output).
   * Implies `disableToolbar`.
   */
  plainText?: boolean
  enableMentions?: boolean
  className?: string
  borderless?: boolean
  disabled?: boolean
  sublabel?: string
  helperText?: string
  noMargin?: boolean
  error?: { message?: string }
  /**
   * Extra Quill format names to allow even when `plainText` is on. Useful for
   * transient composer affordances like a variable-warning background that
   * stay visible while editing but get stripped before save.
   */
  extraPlainTextFormats?: readonly string[]
  /**
   * Optional normalizer applied to both the editor's current semantic HTML
   * and the incoming `value` before comparing during the value-sync effect.
   * Use to ignore transient styles (e.g. composer-only background highlights)
   * so that `setContents` does not fire — which would reset the cursor.
   */
  normalizeForValueSync?: (html: string) => string
}

export interface EditorCoreState {
  uniqueId: string
  quillEditor: Quill | null
  isFocused: boolean
  hasValue: boolean
  /** Ref tracking current cursor position — updated on selection/text changes. */
  cursorPositionRef: React.RefObject<number>
}

export function useEditorCore(props: EditorProps & CommonEditorProps) {
  const { onChange, value, readOnly, disabled } = props

  const [isFocused, setIsFocused] = useState(false)
  const [quillEditor, setQuillEditor] = useState<Quill | null>(null)
  const cursorPositionRef = useRef(0)
  const hasValue =
    value !== '' && value !== '<p><br></p>' && value !== '<p></p>'

  // We need to generate a unique ID for the toolbar container since multiple can coexist on the same page
  const uniqueId = useMemo(() => Math.random().toString(36).substring(7), [])

  const api = useApiService()
  const { data: users } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: false,
  })
  const { user } = useAuth()

  // Refs so the mention `source` callback can read the latest users /
  // current user without forcing the Quill init effect to wait for the
  // user query to resolve. Without these the editor wouldn't mount
  // until `useGetUserOptions` finished, which on a cold refresh leaves
  // the wrapper as an empty white box for several hundred ms.
  const usersRef = useRef(users)
  usersRef.current = users
  const currentUserIdRef = useRef(user?.id)
  currentUserIdRef.current = user?.id

  const onChangeHandler = (value: string) => {
    let newValue: string = value
    if (newValue === '<p><br></p>') {
      // empty string on the WYSIWYG editor
      newValue = ''
    }
    onChange?.(newValue)
  }

  useEffect(() => {
    const modules: Record<string, unknown> = {
      toolbar: props.disableToolbar
        ? false
        : {
            container: `#toolbar-${uniqueId}`,
          },
      // Only user edits belong on Quill's native undo stack. Without this,
      // the programmatic `setContents` that loads initial/external value is
      // recorded too, so a user pressing Cmd+Z right after focusing the
      // editor "undoes" the content load and wipes the whole body (FIR-7544).
      history: {
        userOnly: true,
      },
    }

    if (props.enableMentions) {
      modules.mention = {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        source: function (searchTerm: string, renderList: any) {
          const MAX_OPTIONS = 5
          const currentUsers = usersRef.current
          const currentUserId = currentUserIdRef.current
          const userOptions = currentUsers
            ?.filter((u) => u.value !== currentUserId)
            .map((u) => ({
              id: u.value,
              value: u.label,
            }))
          const values = userOptions ?? []

          if (searchTerm.length === 0) {
            renderList(values.slice(0, MAX_OPTIONS), searchTerm)
          } else {
            const matches = []
            for (let i = 0; i < values.length; i++) {
              if (
                values[i].value.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                matches.push(values[i])
              }
            }
            renderList(matches.slice(0, MAX_OPTIONS), searchTerm)
          }
        },
      }
    }

    const editor = new Quill(`#editor-${uniqueId}`, {
      readOnly: readOnly || disabled,
      theme: props.theme ?? 'snow',
      placeholder: props.placeholder ?? 'Add a new note here',
      modules,
      // Empty formats whitelist strips bold/italic/color/etc. from input
      // and paste — matches what a plain-text email body can actually carry.
      // `extraPlainTextFormats` is the escape hatch for transient composer
      // affordances (e.g. variable-warning highlights stripped before save).
      ...(props.plainText
        ? { formats: [...(props.extraPlainTextFormats ?? [])] }
        : {}),
    })

    setQuillEditor(editor)

    editor.on('text-change', () => {
      onChangeHandler(editor.getSemanticHTML())
      const sel = editor.getSelection()
      if (sel) cursorPositionRef.current = sel.index + sel.length
    })

    editor.on(
      'selection-change',
      (range: { index: number; length: number } | null) => {
        if (range) cursorPositionRef.current = range.index + range.length
      }
    )

    editor.root.addEventListener('focus', () => {
      setIsFocused(true)
    })

    editor.root.addEventListener('blur', () => {
      setIsFocused(false)
    })

    if (value) {
      editor.setContents(
        editor.clipboard.convert({
          html: value,
        })
      )
    }

    return () => {
      editor.root.removeEventListener('focus', () => {
        setIsFocused(true)
      })
      editor.root.removeEventListener('blur', () => {
        setIsFocused(false)
      })
    }
    // Init once on mount — mention `source` reads users via ref so we
    // don't need to re-create the Quill instance when users resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!quillEditor) return

    const currentContent = quillEditor.getSemanticHTML()
    const initialQuillValue = '<p><br></p>'
    const normalizeFn = props.normalizeForValueSync ?? ((s: string) => s)
    const normalizedCurrentContent = normalizeFn(
      currentContent === initialQuillValue ? '' : currentContent
    )
    const normalizedValue = normalizeFn(value || '')

    if (normalizedCurrentContent !== normalizedValue) {
      if ((value || '') === '') {
        quillEditor.setText('')
      } else {
        quillEditor.setContents(
          quillEditor.clipboard.convert({
            html: value || '',
          })
        )
      }
    }
  }, [value, quillEditor, props.normalizeForValueSync])

  return {
    uniqueId,
    quillEditor,
    isFocused,
    hasValue,
    cursorPositionRef,
  }
}

export function EditorToolbar(props: {
  uniqueId: string
  disableToolbar?: boolean
  plainText?: boolean
  additionalToolbarButtons?: React.ReactNode
}) {
  const { uniqueId, disableToolbar, plainText, additionalToolbarButtons } =
    props

  if (disableToolbar) {
    return null
  }

  // Plain-text mode hides the formatting buttons (no formats render in
  // text/plain) but keeps the toolbar container and any custom buttons
  // like the variable-insert dropdown — those work in plain text too.
  if (plainText) {
    return (
      <div
        id={`toolbar-${uniqueId}`}
        className="flex items-center gap-2 pl-3 py-1.5 text-xxs uppercase tracking-wider text-fg-secondary"
      >
        <span>Plain text — formatting disabled</span>
        {additionalToolbarButtons && (
          <span className="ml-auto flex items-center pr-24">
            {additionalToolbarButtons}
          </span>
        )}
      </div>
    )
  }

  return (
    <div id={`toolbar-${uniqueId}`}>
      <button className="ql-bold"></button>
      <button className="ql-italic"></button>
      <button className="ql-underline"></button>
      <button className="ql-strike"></button>
      <button className="ql-list" value="bullet"></button>
      <button className="ql-list" value="ordered"></button>
      {additionalToolbarButtons && (
        <span className="ml-auto flex items-center">
          {additionalToolbarButtons}
        </span>
      )}
    </div>
  )
}
